import React from 'react';
import { Link } from 'react-router-dom';
import {
  LANDING_ACCENT_STYLES,
  LANDING_CONFIG,
  LANDING_MIN_ROLE,
  LandingMinRole,
} from '../../constants';
import { useAppSelector } from '../../store';
import { hasAdminAccess, hasSuperAdminAccess } from '../../utils/auth.utils';

const IconComponent: React.FC<{ type: string; className?: string }> = ({ type, className = "" }) => {
  const icons = {
    plus: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    ),
    document: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    ),
    arrow: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
    ),
    config: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    ),
    analytics: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3v18h18M7 15l4-4 4 4 5-6" />
    ),
    clients: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    ),
    users: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    )
  };

  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {icons[type as keyof typeof icons] || icons.config}
    </svg>
  );
};

const canSeeTool = (
  minRole: LandingMinRole,
  isAdmin: boolean,
  isSuperAdmin: boolean,
): boolean => {
  if (minRole === LANDING_MIN_ROLE.SUPERADMIN) return isSuperAdmin;
  if (minRole === LANDING_MIN_ROLE.ADMIN) return isAdmin;
  return true;
};

export const LandingPage: React.FC = () => {
  const authUser = useAppSelector(state => state.auth.user);
  const isAdmin = hasAdminAccess(authUser?.role);
  const isSuperAdmin = hasSuperAdminAccess(authUser?.role);

  const visibleAdminTools = LANDING_CONFIG.adminTools.filter(tool =>
    canSeeTool(tool.minRole, isAdmin, isSuperAdmin),
  );

  const greeting = authUser?.name ? `Welcome back, ${authUser.name.split(' ')[0]}` : 'Welcome';

  return (
    <div
      className="relative isolate overflow-hidden min-h-screen bg-slate-50 text-slate-900"
      style={{ fontFamily: '"Space Grotesk","Plus Jakarta Sans",system-ui,sans-serif' }}
    >
      <style>{`
        @keyframes landing-fade {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .landing-fade { animation: landing-fade 700ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        .landing-fade.delay-1 { animation-delay: 120ms; }
        .landing-fade.delay-2 { animation-delay: 240ms; }
        .landing-fade.delay-3 { animation-delay: 360ms; }
        @media (prefers-reduced-motion: reduce) {
          .landing-fade { animation: none; }
        }
      `}</style>

      {/* Base wash */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white via-slate-50 to-slate-100/90" />

      {/* Top warm atmospheric glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(85%_55%_at_50%_0%,rgba(251,146,60,0.14)_0%,rgba(251,146,60,0.04)_35%,transparent_65%)]" />

      {/* Soft right accent */}
      <div className="pointer-events-none absolute -right-40 top-40 h-[460px] w-[460px] rounded-full bg-orange-300/25 blur-[160px]" />

      {/* Soft left accent */}
      <div className="pointer-events-none absolute -left-40 top-[480px] h-[420px] w-[420px] rounded-full bg-sky-200/30 blur-[170px]" />

      {/* Dot grid with fade mask */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] [background-size:28px_28px]"
        style={{
          WebkitMaskImage:
            'linear-gradient(to bottom, black 0%, black 35%, transparent 85%)',
          maskImage:
            'linear-gradient(to bottom, black 0%, black 35%, transparent 85%)'
        }}
      />

      <div className="relative max-w-6xl 3xl:max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-20 lg:pt-24 pb-20 md:pb-24">
        {/* Hero */}
        <section className="text-center landing-fade max-w-3xl 3xl:max-w-4xl mx-auto">
          <span className="inline-flex items-center gap-2.5 rounded-full border border-slate-200/80 bg-white/70 backdrop-blur-md px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <span className="relative flex h-1.5 w-1.5" aria-hidden="true">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
            </span>
            <span className="tracking-wide">{greeting}</span>
          </span>

          <h1 className="mt-6 text-4xl md:text-5xl 3xl:text-6xl font-semibold text-slate-900 leading-[1.02] tracking-[-0.025em]">
            {LANDING_CONFIG.hero.title}
            <span className="block mt-1.5 text-3xl md:text-4xl 3xl:text-5xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent">
              {LANDING_CONFIG.hero.subtitle}
            </span>
          </h1>

          <p className="mt-6 text-base md:text-lg 3xl:text-xl text-slate-600 max-w-xl 3xl:max-w-2xl mx-auto leading-relaxed">
            {LANDING_CONFIG.hero.description}
          </p>
        </section>

        {/* Primary Actions */}
        <section
          aria-label="Primary actions"
          className="mt-14 md:mt-16 3xl:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 3xl:gap-6 landing-fade delay-1"
        >
          {LANDING_CONFIG.primaryCards.map(card => {
            const accent = LANDING_ACCENT_STYLES[card.accent] ?? LANDING_ACCENT_STYLES.rose;
            return (
              <Link
                key={card.id}
                to={card.route}
                className="group relative rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50"
              >
                <div className="relative h-full overflow-hidden rounded-2xl border border-slate-200/70 bg-white/70 backdrop-blur-sm p-6 3xl:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_4px_16px_rgba(15,23,42,0.04)] transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:border-slate-300/80 group-hover:bg-white group-hover:shadow-[0_4px_12px_rgba(15,23,42,0.06),0_24px_48px_rgba(15,23,42,0.10)]">
                  {/* Top gradient accent line — fades in on hover */}
                  <div
                    className={`absolute inset-x-4 top-0 h-px bg-gradient-to-r from-transparent ${accent.hoverGlow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                    aria-hidden="true"
                  />

                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5 min-w-0">
                      <div
                        className={`shrink-0 inline-flex items-center justify-center h-12 w-12 3xl:h-14 3xl:w-14 rounded-xl border ${accent.chipBg} ${accent.chipBorder} shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] transition-transform duration-300 group-hover:scale-[1.04]`}
                      >
                        <IconComponent
                          type={card.icon}
                          className={`h-6 w-6 3xl:h-7 3xl:w-7 ${card.iconColor}`}
                        />
                      </div>
                      <h2 className="text-lg 3xl:text-xl font-semibold text-slate-900 tracking-tight truncate">
                        {card.title}
                      </h2>
                    </div>
                    <IconComponent
                      type="arrow"
                      className="shrink-0 h-4 w-4 text-slate-300 transition-all duration-300 group-hover:text-slate-500 group-hover:translate-x-1"
                    />
                  </div>

                  <p className="mt-4 text-sm 3xl:text-base text-slate-600 leading-relaxed">
                    {card.description}
                  </p>

                  <div className={`mt-6 inline-flex items-center text-sm 3xl:text-base font-medium ${card.iconColor}`}>
                    <span>{card.cta}</span>
                    <IconComponent
                      type="arrow"
                      className="ml-1.5 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {/* Admin Tools */}
        {visibleAdminTools.length > 0 && (
          <section aria-label="Admin tools" className="mt-20 md:mt-24 landing-fade delay-2">
            <div className="flex items-center gap-4 mb-5">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
                Admin Tools
              </h3>
              <div
                className="flex-1 h-px bg-gradient-to-r from-slate-200 via-slate-200/70 to-transparent"
                aria-hidden="true"
              />
              <span className="text-[11px] font-medium text-slate-400">
                {visibleAdminTools.length} available
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 3xl:gap-5">
              {visibleAdminTools.map(tool => {
                const accent = LANDING_ACCENT_STYLES[tool.accent] ?? LANDING_ACCENT_STYLES.rose;
                return (
                  <Link
                    key={tool.id}
                    to={tool.route}
                    className="group relative rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-slate-900/20 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50"
                  >
                    <div className="relative h-full rounded-xl border border-slate-200/60 bg-white/60 backdrop-blur-sm p-5 3xl:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:border-slate-300/80 group-hover:bg-white group-hover:shadow-[0_2px_8px_rgba(15,23,42,0.05),0_12px_24px_rgba(15,23,42,0.06)]">
                      <div className="flex items-start gap-3.5">
                        <div
                          className={`shrink-0 inline-flex items-center justify-center h-10 w-10 3xl:h-11 3xl:w-11 rounded-lg border ${accent.chipBg} ${accent.chipBorder} shadow-[inset_0_1px_0_rgba(255,255,255,0.6)] transition-transform duration-300 group-hover:scale-[1.04]`}
                        >
                          <IconComponent
                            type={tool.icon}
                            className={`h-[18px] w-[18px] 3xl:h-5 3xl:w-5 ${accent.iconColor}`}
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4
                            className={`text-sm 3xl:text-base font-semibold text-slate-900 tracking-tight transition-colors ${accent.text}`}
                          >
                            {tool.title}
                          </h4>
                          <p className="mt-1 text-xs 3xl:text-sm text-slate-500 leading-relaxed">
                            {tool.description}
                          </p>
                        </div>
                        <IconComponent
                          type="arrow"
                          className="h-3.5 w-3.5 text-slate-300 transition-all duration-300 group-hover:text-slate-500 group-hover:translate-x-0.5"
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};
